<template>
    <v-card flat style="border-top: 1px solid lightgray">

        <v-overlay :value="loadingShipmentList">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <v-expansion-panels v-model="currentPanel">
            <v-expansion-panel
                    v-for="(shipmentItems, direction, index) in shipments"
                    :key="direction"
                    :style="{
                    background: currentPanel === index ? '#E6E6E6' : '#FFFFFF'
                }"
            >
                <v-expansion-panel-header>
                    <div
                            :style="{
                                'text-decoration': currentPanel === index ? 'underline' : 'none',
                                'font-weight': 'bold'
                            }"
                    >
                        {{ `${ t(capitalizeFirstLetter(direction)) } (${ shipmentItems.length })` }}
                        <v-btn
                                text
                                class="ml-1 mb-1"
                                color="primary"
                                style="min-width: 0; height: 2rem; padding: 4px 8px; font-size: 12px;"
                                @click.stop="openGeneralCalendar(direction)"
                        >
                            <v-icon color="bal-red" style="font-size: 16px;">
                                fas fa-calendar-day
                            </v-icon>
                        </v-btn>
                    </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content
                        :style="{
                        background: currentPanel === index ? '#E6E6E6' : '#FFFFFF'
                    }"
                >
                    <v-data-table
                            :options="tableOptions"
                            :items-per-page="50"
                            :footer-props="footerProps"
                            :search="searchShipmentString"
                            class="shipments-table"
                            dense
                            :headers="tableHeaders"
                            :items="shipmentItems"
                            item-key="id"
                    >
                        <template v-slot:group.header="{ group, headers, toggle, isOpen, item }">
                            <td :colspan="headers.length" class="pl-1 font-weight-bold">
                                <v-btn :ref="group" :data-open="isOpen" icon small @click="toggle">
                                    <v-icon v-if="isOpen">arrow_drop_down</v-icon>
                                    <v-icon v-else>arrow_drop_up</v-icon>
                                </v-btn>
                                {{ formatDate(group) }}
                            </td>
                        </template>

                        <template v-slot:item="{ item }">
                            <tr
                                    :key="'shipment_' + item.id"
                                    @click="handleTableRowClick(item)"
                                    :class="{ activeTableRow: activeShipment && parseInt(item.id) === parseInt(activeShipment.id) }"
                            >
                                <td class="text-left" style="width: 25%">{{
                                        item.company_name ? item.company_name : "-"
                                    }}
                                </td>
                                <td class="text-left" style="width: 7%">{{ item.vehicle_reg_number || "-" }}</td>
                                <td class="text-left" style="width: 7%">{{
                                        item.start ? formatDate(item.start) : "-"
                                    }}
                                </td>
                                <td class="text-left" style="width: 7%">{{
                                        item.start ? formatTime(item.start) : "-"
                                    }}
                                </td>
                                <td class="text-left" style="width: 7%">{{ item.end ? formatTime(item.end) : "-" }}</td>
                                <td class="text-left" style="width: 7%">
                                    {{ getDuration(item.shipment_size, item.direction) || "-" }}
                                </td>
                                <td class="text-left">{{
                                        t(getShipmentSizeText(item.shipment_size, item.direction)) || "-"
                                    }}
                                </td>
                                <td class="text-left">
                                  <span :style="{ color: item.status === 'waiting' ? 'red' : item.status === 'done' ? 'green' : 'inherit', fontWeight: 'bold' }">
                                    {{ t(capitalizeFirstLetter(item.status)) || "-" }}
                                  </span>
                                </td>
                                <td class="text-left" style="width: 7%">{{ item.created_by_display || "-" }}</td>
                                <td class="text-right" style="width: 7%">
                                    <v-icon 
                                            style="cursor:pointer;"
                                            :color="item.status === 'waiting' ? 'green' : 'bal-red'"
                                            @click.stop="handleToggleDoneClick(item.id)"
                                            small
                                    >
                                        {{ item.status === "waiting" ? "fal fa-check-square" : "fal fa-minus-square" }}
                                    </v-icon>

                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel
                    v-for="(pendingShipmentItems, pendingDirection, pendingIndex) in pendingShipments"
                    :key="pendingDirection+'_pending'"
                    :style="{
                    background: currentPanel === pendingIndex + shipments.length ? '#E6E6E6' : '#FFFFFF'
                }"
            >
                <v-expansion-panel-header>
                    <div
                            :style="{
                            'text-decoration': currentPanel === pendingIndex + shipments.length ? 'underline' : 'none',
                            'font-weight': 'bold'
                        }"
                    >
                        {{
                            `${ t('Pending input') } - ${ t(capitalizeFirstLetter(pendingDirection)) } (${ pendingShipmentItems.length })`
                        }}

                    </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content
                        :style="{
                        background: currentPanel === pendingIndex + shipments.length ? '#E6E6E6' : '#FFFFFF'
                    }"
                >
                    <v-data-table
                            :items-per-page="50"
                            :footer-props="footerProps"
                            :search="searchShipmentString"
                            class="shipments-table"
                            dense
                            :headers="tableHeadersPending"
                            :items="pendingShipmentItems"
                            item-key="id"
                    >
                        <template v-slot:item="{ item }">
                            <tr :key="'shipment_' + item.id">
                                <td class="text-left" style="width: 25%">
                                    {{ item.company_name ? item.company_name : "-" }}
                                </td>
                                <td class="text-left" style="width: 15%">{{ item.created_by_display || "-" }}</td>
                                <td class="text-left" style="width: 15%">
                                    {{ item.public_token_expires_at ? formatDate(item.public_token_expires_at) : "-" }}
                                </td>
                                <td class="text-left" style="width: 30%">
                                    <span
                                            v-if="item.url"
                                            @click="copyToClipboard(item.url)"
                                            class="copy-text"
                                    >
                                       {{ t("Copy") }}
                                        <v-icon class="ml-1 copy-icon">fal fa-clipboard</v-icon>
                                    </span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <general-shipments-calendar-modal
                v-if="showGeneralCalendarModal"
                :direction="generalCalendarDirection"
                @close="closeGeneralCalendar"
                :show="showGeneralCalendarModal"
        ></general-shipments-calendar-modal>

    </v-card>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from "vuex";
import { PermissionsConstants } from "@/permissions";
import { helperMixin } from "@/mixins/helper-mixin";
import GeneralShipmentsCalendarModal from "@/pages/shipment/tab-items/dialog/general-shipments-calendar-modal.vue";

export default {
    name: "shipment-list",
    components: { GeneralShipmentsCalendarModal },
    mixins: [helperMixin],
    data() {
        return {
            showGeneralCalendarModal: false,
            generalCalendarDirection: null,
            currentPanel: 0,
            tableOptions: {
                sortBy: ["status", "start"],
                sortDesc: [true, true],
                groupBy: ["group_by_date"],
                groupDesc: [true]
            },
            footerProps: { "items-per-page-options": [25, 50, 100] },
            tableHeaders: [
                {
                    text: this.t("Company name"),
                    align: "left",
                    value: "company_name",
                    sortable: false
                },
                {
                    text: this.t("Car nr"),
                    align: "left",
                    value: "vehicle_reg_number",
                    sortable: false
                },
                {
                    text: this.t("Date"),
                    align: "left",
                    value: "start",
                    sortable: false
                },
                {
                    text: this.t("Start time"),
                    align: "left",
                    value: "start",
                    sortable: false
                },
                {
                    text: this.t("End time"),
                    align: "left",
                    value: "end",
                    sortable: false
                },
                {
                    text: this.t("Duration"),
                    align: "left",
                    value: "shipment_size",
                    sortable: false
                },
                {
                    text: this.t("Shipment size"),
                    align: "left",
                    value: "shipment_size",
                    sortable: false
                },
                {
                    text: this.t("Status"),
                    align: "left",
                    value: "status",
                    sortable: false
                },
                {
                    text: this.t("Created by"),
                    align: "left",
                    value: "created_by_display",
                    sortable: false
                },
                {
                    text: '',
                    align: "left",
                    value: "id",
                    sortable: false
                }
            ],
            tableHeadersPending: [
                {
                    text: this.t("Company name"),
                    align: "left",
                    value: "company_name",
                    sortable: false
                },
                {
                    text: this.t("Created by"),
                    align: "left",
                    value: "created_by_display",
                    sortable: false
                },
                {
                    text: this.t("Expires at"),
                    align: "left",
                    value: "public_token_expires_at",
                    sortable: false
                },
                {
                    text: this.t("Link"),
                    align: "left",
                    value: "url",
                    sortable: false
                },
            ]
        };
    },
    async mounted() {
        await this.getShipmentList()
    },
    computed: {
        ...mapState("ShipmentStore", ["shipments", "pendingShipments", "loadingShipmentList", "activeShipment", "searchShipmentString", "sizes"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        }
    },
    methods: {
        ...mapActions("ShipmentStore", ["getShipmentList", "getShipmentData", "toggleStatus"]),
        ...mapMutations("ShipmentStore", ["setRequestSuccess", "setRequestError"]),
        handleTableRowClick(shipment) {
            if (!this.hasPermissionForAction(PermissionsConstants.SHIPMENT, PermissionsConstants.VIEW)) {
                return;
            }

            if (this.$route.name !== "shipment-view") {
                this.$router.push({ name: "shipment-view", params: { shipmentId: shipment.id } });
                return;
            }
            if (this.$route.name === "shipment-view" && parseInt(shipment.id) !== parseInt(this.$route.params.shipmentId)) {
                this.$router.push({ name: "shipment-view", params: { shipmentId: shipment.id } });
                this.getShipmentData(shipment.id)
            }
        },
        getDuration(shipment_size_id, direction) {
            const sizeOptions = this.sizes[direction] || {};
            const entry = Object.values(sizeOptions).find(option => option.id === shipment_size_id);

            if (entry) {
                const hours = Math.floor(entry.value / 60);
                const minutes = entry.value % 60;
                return `${ hours.toString().padStart(2, "0") }:${ minutes.toString().padStart(2, "0") }`;
            }
            return null;
        },
        getShipmentSizeText(shipment_size_id, direction) {
            const sizeOptions = this.sizes[direction] || {};
            const entry = Object.values(sizeOptions).find(option => option.id === shipment_size_id);
            return entry ? entry.text : null;
        },
        openGeneralCalendar(direction) {
            this.showGeneralCalendarModal = true
            this.generalCalendarDirection = direction
        },
        closeGeneralCalendar() {
            this.showGeneralCalendarModal = false
            this.generalCalendarDirection = null

        },
        handleToggleDoneClick(shipmentId){
            this.toggleStatus({ shipmentId:shipmentId, fetchItemData:false })
        },
        copyToClipboard(text) {
            navigator.clipboard.writeText(text)
                    .then(() => {
                        this.setRequestError(false);
                        this.setRequestSuccess(true);
                        setTimeout(() => this.setRequestSuccess(false), 2500);
                    })
                    .catch(() => {
                        this.setRequestSuccess(false);
                        this.setRequestError(true);
                        setTimeout(() => this.setRequestError(false), 2500);
                    });
        }
    }
};
</script>

<style scoped>
.activeTableRow {
    background-color: #f41823;
    color: #fff;
}

.activeTableRow:hover {
    background-color: #e11520 !important;
    color: #fff !important;
}

.dot {
    margin-top: 4px;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    display: inline-block;
}

.v-data-table-header {
    background-color: #f0f0f0;
}

::v-deep .v-data-table-header th {
    white-space: nowrap;
}

td {
    height: 25px !important;
}

.copy-text {
    cursor: pointer;
    display: flex;
    align-items: center;
    color: #42A5F5;
    font-weight: 500;
}

.copy-icon {
    color: #42A5F5;
    font-weight: 500;
    font-size: 14px;
}
</style>
